/**
 * What the CRF Builder writes for its session, and when it needs to write it.
 *
 * MEASURED 2026-10-11 on Clinical Dev: merely OPENING the page rewrote the whole
 * session and bumped its stored version, because the auto-save effect fires once
 * on load with the forms it has just read. A second open copy of the page then
 * made the first copy's next save stale. The page now remembers a fingerprint of
 * the content it last loaded or saved and writes only when the content differs.
 *
 * Pure (no React, no Firebase) so it is tested from functions' vitest.
 */
import { assignFormCodes } from './assignFormCodes';
import { carriedQuestionFields } from '../../pages/project/crfCarriedFields';
import { stripUndefined } from './sessionCopy';
import type { CRFSession } from '../../pages/project/CRFBuilder';

/**
 * The session as it is stored. `assignCodes` is false only when fingerprinting
 * what is ALREADY stored: a stored form with no code must then read as lacking one,
 * so that the first load which has to assign codes counts as a change and saves once.
 */
export function buildCleanSession(session: CRFSession, assignCodes = true): CRFSession {
  // CODES ARE ASSIGNED HERE, NOT ONLY AT GENERATION.
  //
  // Measured 2026-08-28 on CLEANRUN-T5: assignFormCodes ran at suggestion time
  // and the store held all 34 codes; after variable generation and approval the
  // stored `code` was empty on all 34. The assignment lived in React state and
  // some path between rebuilt the forms without it. Rather than chase every such
  // path — and leave the next one to reintroduce it — persistence is made a
  // property of the SAVE, which every path goes through.
  //
  // Idempotent by construction: a form that already carries an explicit code
  // keeps it, so this never renames a form a user or an earlier run named.
  const codedForms = assignCodes ? assignFormCodes(session.forms) : session.forms;
  return {
    protocolTitle: session.protocolTitle,
    protocolText: session.protocolText,
    ...(session.edcSystemAtGeneration ? { edcSystemAtGeneration: session.edcSystemAtGeneration } : {}),
    forms: codedForms.map(f => ({
      id: f.id,
      name: f.name,
      rationale: f.rationale,
      category: f.category,
      status: f.status,
      // The stored version this copy is based on. A reload trusts a Draft's
      // questions from here only while that is still the stored version
      // (mergeServerWithCache).
      ...(f.serverUpdatedAt ? { serverUpdatedAt: f.serverUpdatedAt } : {}),
      variables: f.variables
        ? f.variables.map(v => ({
            id: v.id,
            varName: v.varName,
            questionText: v.questionText,
            responseType: v.responseType,
            options: v.options,
            unit: v.unit,
            ...(v.ct_codelist_ref ? { ct_codelist_ref: v.ct_codelist_ref } : {}),
            ...(v.ct_codelist_version ? { ct_codelist_version: v.ct_codelist_version } : {}),
            ...(v.ct_match_confidence ? { ct_match_confidence: v.ct_match_confidence } : {}),
            // Margin content and the dictionary's details: a draft lives here
            // until it is approved, so dropping these would lose them on reload.
            ...carriedQuestionFields(v),
          }))
        : null,
      ...(f.content ? { content: f.content } : {}),
      // The page structure the generator chose, including the form-level
      // instruction line the content pass writes into it. A Draft form lives
      // only here until approved, so without this a reload erased both.
      ...(f.structure ? { structure: f.structure } : {}),
      ...(f.licensedInstrument ? { licensedInstrument: f.licensedInstrument } : {}),
      ...(f.dismissedWarnings?.length ? { dismissedWarnings: f.dismissedWarnings } : {}),
      // Always present now: assignFormCodes above guarantees a value.
      code: f.code,
      // SIBLINGS OF THE SAME SHAPE, found by looking at what else this
      // serialiser drops. Both are decisions/provenance held only in memory:
      // `sourceProvenance` records that a form came from a published standard,
      // and `reuseDeclined` records that the user turned that offer down. Losing
      // them on reload re-offers a form the user already declined and erases the
      // record of where an imported form came from.
      ...(f.sourceProvenance ? { sourceProvenance: f.sourceProvenance } : {}),
      ...(f.reuseDeclined ? { reuseDeclined: f.reuseDeclined } : {}),
      // Persist section structure so a reorder's flat order + membership
      // survives the localStorage/Firestore session cache on reload.
      ...(f.sections ? { sections: f.sections } : {}),
    })),
  };
}

function stable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === 'object' && !(value instanceof Date)) {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value as Record<string, unknown>).sort()) out[k] = stable((value as Record<string, unknown>)[k]);
    return out;
  }
  return value;
}

/**
 * A string that is equal for equal content. Keys are sorted, because Firestore hands fields
 * back in its own order, and undefined fields are dropped, as the write drops them. The
 * volatile fields (updatedAt, docVersion) are not part of the cleaned session, so they are
 * not part of this.
 */
export function sessionFingerprint(clean: CRFSession): string {
  return JSON.stringify(stable(stripUndefined(clean)));
}

/** The fingerprint of a session read from storage (codes as stored, not assigned). */
export function storedSessionFingerprint(stored: CRFSession): string {
  return sessionFingerprint(buildCleanSession(stored, false));
}

/** What the page last loaded or saved. `current` is null when it knows of nothing. */
export interface PersistedFingerprint {
  current: string | null;
  /**
   * What the most recent save attempt came to, set inside the save queue. A recovery resets it to
   * null and reports "saved" only from a 'saved' or 'unchanged' outcome (recoveryWasSaved).
   */
  lastOutcome?: SessionSaveOutcome | null;
}

export type SessionSaveOutcome = 'saved' | 'unchanged' | 'stale' | 'blocked' | 'failed';

/** True only when the save after a recovery was seen to store the content, or the content already was stored. */
export function recoveryWasSaved(persisted: PersistedFingerprint, isStale: boolean): boolean {
  return !isStale && (persisted.lastOutcome === 'saved' || persisted.lastOutcome === 'unchanged');
}

/**
 * Should this content be written? Not when it is exactly what the page last loaded or saved.
 * When it should be, the fingerprint is recorded as the page's new belief.
 */
export function decideSessionWrite(persisted: PersistedFingerprint, clean: CRFSession): { write: boolean; fingerprint: string } {
  const fingerprint = sessionFingerprint(clean);
  if (persisted.current === fingerprint) return { write: false, fingerprint };
  return { write: true, fingerprint };
}

/** The part of VersionedDoc the save needs; a fake stands in for it in tests. */
export interface SessionWriter {
  write(
    task: (next: number) => Promise<void>,
    marker?: string,
    hooks?: {
      gate?: () => boolean;
      settled?: (outcome: 'saved' | 'stale' | 'blocked' | 'skipped' | 'failed') => void;
    },
  ): Promise<'saved' | 'stale' | 'blocked' | 'skipped'>;
}

/**
 * Save the session unless it is exactly what is already stored, and keep the fingerprint true.
 *
 * Both the decision and the bookkeeping happen INSIDE the document's save queue (the gate and
 * settled hooks of VersionedDoc.write):
 *  - the decision is made when the write would run, against the content the earlier queued saves
 *    really stored. Deciding at request time lost a change: stored X, edit to A (write in flight),
 *    undo to X before it lands - X looked unchanged, A landed, and nothing wrote X again;
 *  - the fingerprint is recorded only when the outcome is 'saved' (the content is stored). A stale
 *    or blocked outcome, or a failure, leaves it null, so the same content is written after a
 *    recovery.
 *
 * `beforeWrite` runs only when a write is going to be attempted (the browser copy).
 * Resolves 'unchanged' when nothing was written; rejects when the write failed.
 */
export async function persistSession(opts: {
  persisted: PersistedFingerprint;
  clean: CRFSession;
  version: SessionWriter;
  writeDoc: (next: number) => Promise<void>;
  marker?: string;
  beforeWrite?: () => void;
}): Promise<SessionSaveOutcome> {
  const { persisted, clean, version } = opts;
  const fingerprint = sessionFingerprint(clean);
  const outcome = await version.write(
    async (next) => {
      opts.beforeWrite?.();
      await opts.writeDoc(next);
    },
    opts.marker,
    {
      gate: () => decideSessionWrite(persisted, clean).write,
      settled: (o) => {
        if (o === 'saved') { persisted.current = fingerprint; persisted.lastOutcome = 'saved'; }
        else if (o === 'skipped') { persisted.lastOutcome = 'unchanged'; }
        else { persisted.current = null; persisted.lastOutcome = o; }
      },
    },
  );
  return outcome === 'skipped' ? 'unchanged' : outcome;
}
